import { store } from '../state/store';
import type { Bake, Doneness } from '../types';

// Bakes carry their own running clock measured in "oven seconds" — independent
// of wall time so timers pause cleanly when the player navigates away.
export interface BakeRuntime {
  bake: Bake;
  elapsed: number;       // seconds in oven
  ovenTemp: number;      // current oven temp while this bake is in
  running: boolean;
  tempSamples: { temp: number; weight: number }[]; // weighted sum to detect persistent wrong temp
}

const TEMP_TOLERANCE = 15; // ±15°F is "right"

export class BakingSystem {
  private runtimes = new Map<string, BakeRuntime>();

  ensureRuntime(bake: Bake, ovenTemp: number): BakeRuntime {
    let rt = this.runtimes.get(bake.id);
    if (!rt) {
      rt = { bake, elapsed: 0, ovenTemp, running: false, tempSamples: [] };
      this.runtimes.set(bake.id, rt);
    } else {
      rt.bake = bake;
      rt.ovenTemp = ovenTemp;
    }
    return rt;
  }

  setOvenTemp(temp: number): void {
    for (const rt of this.runtimes.values()) rt.ovenTemp = temp;
  }

  start(bakeId: string): void {
    const rt = this.runtimes.get(bakeId);
    if (rt) rt.running = true;
  }

  reset(bakeId: string): void {
    const rt = this.runtimes.get(bakeId);
    if (!rt) return;
    rt.elapsed = 0;
    rt.running = false;
    rt.tempSamples = [];
    store.updateBakeDoneness(bakeId, 'raw');
  }

  tick(dtSec: number): void {
    for (const rt of this.runtimes.values()) {
      if (!rt.running) continue;
      rt.elapsed += dtSec;
      rt.tempSamples.push({ temp: rt.ovenTemp, weight: dtSec });
      this.recomputeDoneness(rt);
    }
  }

  remaining(bakeId: string): number {
    const rt = this.runtimes.get(bakeId);
    if (!rt) return 0;
    return Math.max(0, rt.bake.durationSec - rt.elapsed);
  }

  runtimeFor(bakeId: string): BakeRuntime | undefined {
    return this.runtimes.get(bakeId);
  }

  forget(bakeId: string): void {
    this.runtimes.delete(bakeId);
  }

  private recomputeDoneness(rt: BakeRuntime): void {
    const targetTemp = store.recipeById(rt.bake.recipe)?.tempF ?? rt.ovenTemp;
    const totalWeight = rt.tempSamples.reduce((s, x) => s + x.weight, 0) || 1;
    const avgTemp = rt.tempSamples.reduce((s, x) => s + x.temp * x.weight, 0) / totalWeight;
    const tempOff = Math.abs(avgTemp - targetTemp) > TEMP_TOLERANCE;
    const dur = rt.bake.durationSec;
    const overrun = rt.elapsed > dur * 1.15;
    const inTheZone = rt.elapsed >= dur * 0.85 && rt.elapsed <= dur * 1.15;

    let doneness: Doneness;
    if (overrun || (tempOff && rt.elapsed > dur * 0.7)) doneness = 'burnt';
    else if (inTheZone && !tempOff) doneness = 'perfect';
    else if (rt.elapsed > 0) doneness = 'baking';
    else doneness = 'raw';

    store.updateBakeDoneness(rt.bake.id, doneness);
  }
}

export const baking = new BakingSystem();
